import type { DocMigration } from "../docMigration";

/** Opening framing fields preserved by this migration (post–hasHeader schema). */
interface WallFramingOpeningJson {
  hasCrippleStudEnds?: boolean;
  hasCrippleStudsBelowOpening?: boolean;
  hasCrippleStudsAboveOpening?: boolean;
  hasTopHeaderSillPlate?: boolean;
  numberOfKingStuds?: number;
  hasHeaderAssembly?: boolean;
  headerAssemblyId?: string;
  numberOfJackStuds?: number;
  roughOpeningBorder?: number;
}

/** Opening framing JSON as stored on docs before this migration runs. */
type LegacyWallFramingOpeningJson = WallFramingOpeningJson & {
  hasHeader?: boolean;
};

interface EntityWithOptionalFraming {
  framing?: LegacyWallFramingOpeningJson;
}

interface MigrationOpeningJson extends EntityWithOptionalFraming {
  readonly entityType?: string;
}

interface LegacyDoorFramingSettingsJson extends LegacyWallFramingOpeningJson {
  readonly type: "DOOR_FRAMING_SETTINGS";
}

interface LegacyWindowFramingSettingsJson extends LegacyWallFramingOpeningJson {
  readonly type: "WINDOW_FRAMING_SETTINGS";
}

interface LegacyDoorDescriptorJson extends EntityWithOptionalFraming {
  readonly id?: string;
}

interface LegacyWindowDescriptorJson extends EntityWithOptionalFraming {
  readonly id?: string;
}

interface LegacyDoorCatalogSettingsJson {
  readonly type: "DOOR_CATALOG_SETTINGS";
  doorTypes: Record<string, LegacyDoorDescriptorJson>;
}

interface LegacyWindowCatalogSettingsJson {
  readonly type: "WINDOW_CATALOG_SETTINGS";
  windowTypes: Record<string, LegacyWindowDescriptorJson>;
}

type MigrationSettingsEntity =
  | LegacyDoorFramingSettingsJson
  | LegacyWindowFramingSettingsJson
  | LegacyDoorCatalogSettingsJson
  | LegacyWindowCatalogSettingsJson;

/** Doc JSON shape this migration reads and mutates. */
export interface MigrationDocJson {
  openings?: Record<string, MigrationOpeningJson>;
  settings?: Record<string, unknown>;
}

export function asMigrationSettingsEntity(value: unknown): MigrationSettingsEntity | undefined {
  if (typeof value !== "object" || value === null || !("type" in value)) {
    return undefined;
  }
  const { type } = value as { type: unknown };
  if (
    type === "DOOR_FRAMING_SETTINGS" ||
    type === "WINDOW_FRAMING_SETTINGS" ||
    type === "DOOR_CATALOG_SETTINGS" ||
    type === "WINDOW_CATALOG_SETTINGS"
  ) {
    return value as MigrationSettingsEntity;
  }
  return undefined;
}

function framingJsonIsEmpty(framing: WallFramingOpeningJson): boolean {
  for (const key in framing) {
    if (Object.prototype.hasOwnProperty.call(framing, key)) {
      return false;
    }
  }
  return true;
}

function migrateLegacyFramingField(
  framing: LegacyWallFramingOpeningJson | undefined,
): WallFramingOpeningJson | undefined {
  if (framing === undefined) {
    return undefined;
  }
  const cleaned: LegacyWallFramingOpeningJson = { ...framing };
  if (cleaned.hasHeader === false) {
    cleaned.hasHeaderAssembly = false;
    delete cleaned.headerAssemblyId;
  }
  delete cleaned.hasHeader;
  return cleaned;
}

function applyFramingCleanup(entity: EntityWithOptionalFraming): void {
  const cleaned = migrateLegacyFramingField(entity.framing);
  if (cleaned === undefined || framingJsonIsEmpty(cleaned)) {
    delete entity.framing;
  } else {
    entity.framing = cleaned;
  }
}

function isLegacyDoorFramingSettings(setting: MigrationSettingsEntity): setting is LegacyDoorFramingSettingsJson {
  return setting.type === "DOOR_FRAMING_SETTINGS";
}

function isLegacyWindowFramingSettings(setting: MigrationSettingsEntity): setting is LegacyWindowFramingSettingsJson {
  return setting.type === "WINDOW_FRAMING_SETTINGS";
}

function isLegacyDoorCatalogSettings(setting: MigrationSettingsEntity): setting is LegacyDoorCatalogSettingsJson {
  return setting.type === "DOOR_CATALOG_SETTINGS";
}

function isLegacyWindowCatalogSettings(setting: MigrationSettingsEntity): setting is LegacyWindowCatalogSettingsJson {
  return setting.type === "WINDOW_CATALOG_SETTINGS";
}

function removeLegacyHasHeaderFromFramingSettings(
  setting: LegacyDoorFramingSettingsJson | LegacyWindowFramingSettingsJson,
): void {
  if (setting.hasHeader === false) {
    setting.hasHeaderAssembly = false;
    delete setting.headerAssemblyId;
  }
  delete setting.hasHeader;
}

function migrateDoc(doc: MigrationDocJson): void {
  const openings = doc.openings;
  if (openings !== undefined) {
    for (const openingId in openings) {
      const opening = openings[openingId];
      if (opening?.entityType === "OPENING") {
        applyFramingCleanup(opening);
      }
    }
  }

  const settings = doc.settings;
  if (settings !== undefined) {
    for (const settingId in settings) {
      const setting = asMigrationSettingsEntity(settings[settingId]);
      if (setting === undefined) {
        continue;
      }
      if (isLegacyDoorFramingSettings(setting) || isLegacyWindowFramingSettings(setting)) {
        removeLegacyHasHeaderFromFramingSettings(setting);
      }
      if (isLegacyDoorCatalogSettings(setting)) {
        const doorTypes = setting.doorTypes;
        for (const doorTypeId in doorTypes) {
          applyFramingCleanup(doorTypes[doorTypeId]);
        }
      }
      if (isLegacyWindowCatalogSettings(setting)) {
        const windowTypes = setting.windowTypes;
        for (const windowTypeId in windowTypes) {
          applyFramingCleanup(windowTypes[windowTypeId]);
        }
      }
    }
  }
}

const migration: DocMigration = {
  id: "20260707_opening_header_assembly_null",
  description: "Replace hasHeader with hasHeaderAssembly",
  apply(json, _warnings, _errors) {
    migrateDoc(json as MigrationDocJson);
    return json;
  },
};

export default migration;
